import { useCallback, useId, useState, type FC, type FormEvent } from "react";
import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogFooter,
    DialogClose,
    DialogBody,
    DialogTitle,
    DialogDescription,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Input } from "@/components/podkit/forms/Input";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { Button } from "@/components/flexkit/Button";
import { useCreateWorkflow } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { TrackLocations } from "@/hooks/use-segment";
import { useNavigate } from "react-router-dom";
import { InputField } from "@/components/podkit/forms/InputField";
import { workflowDetailsEditUrl } from "@/utils/automations";

type Props = {
    open: boolean;
    onClose: () => void;
};

export const CreateWorkflowDialog: FC<Props> = ({ open, onClose }) => {
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-track-location={TrackLocations.CreateWorkflowDialog}>
                <DialogHeader>
                    <DialogTitle>New Automation</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <CreateWorkflowForm onSuccess={onClose} />
            </DialogContent>
        </Dialog>
    );
};

type CreateWorkflowFormProps = {
    onSuccess?: () => void;
};

const CreateWorkflowForm: FC<CreateWorkflowFormProps> = ({ onSuccess }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const nameInputId = useId();
    const descriptionInputId = useId();
    const createWorkflowMutation = useCreateWorkflow();

    const handleSubmit = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            setIsSubmitting(true);
            try {
                const workflow = await createWorkflowMutation.mutateAsync({
                    name: name.trim(),
                    description: description.trim() || undefined,
                });
                onSuccess?.();
                navigate(workflowDetailsEditUrl(workflow));
            } catch (error) {
                toast({
                    title: "Failed to create Workflow",
                    description: formatError(error),
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [name, description, createWorkflowMutation, toast, onSuccess, navigate],
    );

    return (
        <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
                <InputField label="Name" id={nameInputId}>
                    <Input
                        id={nameInputId}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name"
                        required
                        maxLength={80}
                        minLength={1}
                        autoComplete="off"
                        aria-autocomplete="none"
                        disabled={isSubmitting}
                        data-1p-ignore
                        data-lpignore
                        data-protonpass-ignore
                    />
                </InputField>
                <InputField label="Description (optional)" id={descriptionInputId}>
                    <Textarea
                        id={descriptionInputId}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Description"
                        maxLength={500}
                        minHeight={72}
                        maxHeight={216}
                        className="resize-none"
                        disabled={isSubmitting}
                    />
                </InputField>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" disabled={isSubmitting}>
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || !name.trim()}
                    loading={isSubmitting}
                    data-tracking-id="create-workflow-create-workflow-form"
                >
                    Create
                </Button>
            </DialogFooter>
        </form>
    );
};
